import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Sparkles, Target, Trophy, Zap } from 'lucide-react';
import { useProgress } from '../context/ProgressContext';
interface MythGame {
  id: string;
  title: string;
  description: string;
  myths: {
    statement: string;
    isMyth: boolean;
    explanation: string;
  }[];
}
const mythGames: MythGame[] = [{
  id: 'puberty-myths',
  title: 'Puberty Myths',
  description: 'Can you spot the myths about body changes?',
  myths: [{
    statement: 'Masturbation causes health problems',
    isMyth: true,
    explanation: "Total myth! Masturbation is completely normal, natural, and safe. It doesn't cause any health issues. The myths about it are just that—myths!"
  }, {
    statement: 'Everyone goes through puberty at different ages',
    isMyth: false,
    explanation: "True! Puberty typically starts between 8-14 years, but everyone's timeline is unique. There's no 'right' age to start!"
  }, {
    statement: 'You can get pregnant from kissing',
    isMyth: true,
    explanation: "Myth! Pregnancy requires specific biological processes that don't happen through kissing. You can't get pregnant from kissing, hugging, or holding hands!"
  }, {
    statement: 'Periods are a normal part of growing up',
    isMyth: false,
    explanation: 'True! Menstruation is a natural, healthy process. About half the population experiences it. Nothing shameful about it!'
  }, {
    statement: 'Using tampons means you lose your virginity',
    isMyth: true,
    explanation: "Myth! Virginity is a social concept, not a medical one. Using tampons or menstrual cups doesn't change anything about your body or identity!"
  }]
}, {
  id: 'consent-myths',
  title: 'Consent Myths',
  description: 'Learn the truth about boundaries and consent',
  myths: [{
    statement: "If someone doesn't say 'no', it means 'yes'",
    isMyth: true,
    explanation: "Myth! Silence is NOT consent. Consent must be an active, enthusiastic 'YES'. Only yes means yes!"
  }, {
    statement: 'You can change your mind even after saying yes',
    isMyth: false,
    explanation: 'True! Consent can be withdrawn at ANY time. Your comfort and boundaries matter more than anything else!'
  }, {
    statement: 'If someone is drunk, they can still give consent',
    isMyth: true,
    explanation: "Myth! Someone who is intoxicated cannot give valid consent. They're not in a state to make informed decisions!"
  }, {
    statement: 'Being in a relationship means you always have to say yes',
    isMyth: true,
    explanation: "Myth! Relationships require ongoing consent. Being in a relationship doesn't mean automatic consent to anything."
  }, {
    statement: 'Consent is an ongoing conversation, not a one-time thing',
    isMyth: false,
    explanation: 'True! Consent must be freely given, reversible, informed, enthusiastic, and specific—every single time!'
  }]
}, {
  id: 'relationship-myths',
  title: 'Relationship Myths',
  description: 'Bust myths about love and relationships',
  myths: [{
    statement: 'Jealousy is a sign of love',
    isMyth: true,
    explanation: 'Myth! While mild jealousy can be normal, extreme jealousy is a red flag. Love is about trust, not control!'
  }, {
    statement: 'Healthy relationships allow you to keep your own friends',
    isMyth: false,
    explanation: 'True! Healthy partners encourage your other relationships. Isolation is a major warning sign!'
  }, {
    statement: 'You should share all your passwords with your partner',
    isMyth: true,
    explanation: 'Myth! You deserve privacy. Healthy relationships are built on trust, not surveillance!'
  }, {
    statement: "It's okay to have different interests from your partner",
    isMyth: false,
    explanation: "True! You don't have to be identical to be compatible. Celebrate your differences!"
  }, {
    statement: 'Real love means never fighting',
    isMyth: true,
    explanation: 'Myth! Disagreements are normal. What matters is how you handle them—with respect and communication!'
  }]
}, {
  id: 'online-safety-myths',
  title: 'Online Safety Myths',
  description: 'Stay safe online by knowing the facts',
  myths: [{
    statement: 'Private accounts mean your posts are completely safe',
    isMyth: true,
    explanation: 'Myth! Even with privacy settings, followers can screenshot and share. Nothing online is truly private!'
  }, {
    statement: 'People can lie about their age and identity online',
    isMyth: false,
    explanation: 'True! Photos can be stolen, ages can be faked. Always be cautious about who you trust online!'
  }, {
    statement: "It's safe to share your phone number with online friends",
    isMyth: true,
    explanation: 'Myth! Your phone number can be used to find you, harass you, or spam you. Keep it private!'
  }, {
    statement: 'You should tell a trusted adult if someone asks for inappropriate photos',
    isMyth: false,
    explanation: "True! Block them immediately and tell an adult. It's illegal and you're not in trouble—they are!"
  }, {
    statement: "Deleting a message means it's gone forever",
    isMyth: true,
    explanation: 'Myth! Screenshots exist, and deleted content can often be recovered. Think before you send!'
  }]
}];
export function MythBustersPage() {
  const navigate = useNavigate();
  const [selectedGame, setSelectedGame] = useState<string | null>(null);
  const [currentMyth, setCurrentMyth] = useState(0);
  const [score, setScore] = useState(0);
  const [showResult, setShowResult] = useState(false);
  const [selectedAnswer, setSelectedAnswer] = useState<boolean | null>(null);
  const [gameComplete, setGameComplete] = useState(false);
  const {
    addPoints
  } = useProgress();
  const game = mythGames.find(g => g.id === selectedGame);
  const myth = game?.myths[currentMyth];
  const handleAnswer = (answer: boolean) => {
    if (showResult || !myth) return;
    setSelectedAnswer(answer);
    setShowResult(true);
    if (answer === myth.isMyth) {
      setScore(score + 1);
      addPoints(10);
    }
  };
  const handleNext = () => {
    if (!game) return;
    if (currentMyth < game.myths.length - 1) {
      setCurrentMyth(currentMyth + 1);
      setShowResult(false);
      setSelectedAnswer(null);
    } else {
      setGameComplete(true);
      const bonusPoints = score === game.myths.length ? 50 : 20;
      addPoints(bonusPoints);
    }
  };
  const resetGame = () => {
    setSelectedGame(null);
    setCurrentMyth(0);
    setScore(0);
    setShowResult(false);
    setSelectedAnswer(null);
    setGameComplete(false);
  };
  if (gameComplete && game) {
    const percentage = Math.round(score / game.myths.length * 100);
    return <div className="min-h-screen w-full bg-gradient-to-br from-yellow-50 to-orange-50 dark:from-slate-900 dark:via-yellow-950 dark:to-orange-950 flex items-center justify-center p-4" data-id="element-560">
        <div className="max-w-2xl w-full bg-white dark:bg-slate-800 rounded-3xl shadow-2xl p-8 text-center" data-id="element-561">
          <div className="text-6xl mb-4 animate-bounce" data-id="element-562">
            {percentage >= 80 ? '🎉' : percentage >= 60 ? '👏' : '💪'}
          </div>
          <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-2" data-id="element-563">
            Myths Busted!
          </h2>
          <p className="text-xl text-gray-600 dark:text-gray-300 mb-6" data-id="element-564">
            You scored {score} out of {game.myths.length} ({percentage}%)
          </p>
          <div className="Background gradient from yellow to orange" data-id="element-565">
            <p className="text-gray-700 dark:text-gray-300 mb-4" data-id="element-566">
              {percentage >= 80 ? "Amazing! You're a myth-busting champion! 🌟" : percentage >= 60 ? 'Great job! Keep learning and growing! 📚' : 'Good effort! Review the facts and try again! 💪'}
            </p>
            {percentage === 100 && <p className="text-lg font-bold text-green-600 dark:text-green-400 mb-2" data-id="element-567">
                Perfect Score! +50 bonus points! ⭐
              </p>}
          </div>
          <div className="flex gap-4 justify-center" data-id="element-568">
            <button onClick={resetGame} className="px-8 py-3 bg-gradient-to-r from-yellow-600 to-orange-600 text-white font-semibold rounded-xl hover:shadow-lg transition-all" data-id="element-569">
              Play Another Game
            </button>
            <button onClick={() => navigate('/home')} className="Gray button with yellow background, dark mode" data-id="element-570">
              Back Home
            </button>
          </div>
        </div>
      </div>;
  }
  if (selectedGame && myth) {
    return <div className="min-h-screen w-full bg-gradient-to-br from-yellow-900 via-orange-900 to-red-900 flex items-center justify-center p-4" data-id="element-571">
        <div className="max-w-3xl w-full bg-white dark:bg-slate-800 rounded-3xl shadow-2xl overflow-hidden" data-id="element-572">
          {/* Header */}
          <div className="bg-gradient-to-r from-yellow-600 to-orange-600 p-4 flex items-center justify-between" data-id="element-573">
            <div className="text-white" data-id="element-574">
              <span className="text-sm font-medium" data-id="element-575">
                Statement {currentMyth + 1} of {game.myths.length}
              </span>
              <div className="w-48 h-2 bg-white/20 rounded-full mt-1" data-id="element-576">
                <div className="h-full bg-white rounded-full transition-all" style={{
                width: `${(currentMyth + 1) / game.myths.length * 100}%`
              }} data-id="element-577" />
              </div>
            </div>
            <div className="text-white text-right" data-id="element-578">
              <p className="text-sm font-medium" data-id="element-579">Score</p>
              <p className="text-2xl font-bold" data-id="element-580">{score}</p>
            </div>
          </div>
          {/* Question */}
          <div className="p-8" data-id="element-581">
            <div className="flex items-center space-x-2 mb-6" data-id="element-582">
              <Target className="w-6 h-6 text-orange-500" data-id="element-583" />
              <h3 className="text-lg font-semibold text-gray-600 dark:text-gray-300" data-id="element-584">
                Is this a MYTH or FACT?
              </h3>
            </div>
            <p className="text-2xl font-bold text-gray-900 dark:text-white mb-8" data-id="element-585">
              "{myth.statement}"
            </p>
            {/* Buttons */}
            {!showResult && <div className="grid grid-cols-2 gap-4 mb-6" data-id="element-586">
                <button onClick={() => handleAnswer(true)} className="Red button with white text, dark mode" data-id="element-587">
                  MYTH 🚫
                </button>
                <button onClick={() => handleAnswer(false)} className="Green button with white text, dark mode" data-id="element-588">
                  FACT ✓
                </button>
              </div>}
            {/* Result */}
            {showResult && <>
                <div className={`p-6 rounded-xl mb-6 ${selectedAnswer === myth.isMyth ? 'bg-green-50 dark:bg-green-900/20 border-2 border-green-500' : 'bg-red-50 dark:bg-red-900/20 border-2 border-red-500'}`} data-id="element-589">
                  <p className={`font-bold text-lg mb-2 ${selectedAnswer === myth.isMyth ? 'text-green-700 dark:text-green-300' : 'text-red-700 dark:text-red-300'}`} data-id="element-590">
                    {selectedAnswer === myth.isMyth ? '✓ Correct!' : '✗ Not quite!'}
                  </p>
                  <p className="text-gray-700 dark:text-gray-300" data-id="element-591">
                    {myth.explanation}
                  </p>
                </div>
                <button onClick={handleNext} className="w-full py-3 bg-gradient-to-r from-yellow-600 to-orange-600 text-white font-semibold rounded-xl hover:shadow-lg transition-all" data-id="element-592">
                  {currentMyth < game.myths.length - 1 ? 'Next Statement →' : 'See Results 🎉'}
                </button>
              </>}
          </div>
        </div>
      </div>;
  }
  return <div className="Background gradient from yellow to orange with padding" data-id="element-593">
      {/* Header */}
      <header className="White background with dark mode, border and blurring" data-id="element-594">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between" data-id="element-595">
          <button onClick={() => navigate('/home')} className="Icon text with hover effect, dark mode" data-id="element-596">
            <ArrowLeft className="w-5 h-5" data-id="element-597" />
            <span className="font-medium" data-id="element-598">Back</span>
          </button>
          <h1 className="text-lg font-bold text-gray-900 dark:text-white" data-id="element-599">
            Myth Busters
          </h1>
          <div className="w-20" data-id="element-600" />
        </div>
      </header>
      <div className="max-w-6xl mx-auto px-4 py-8" data-id="element-601">
        <div className="text-center mb-8" data-id="element-602">
          <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-3 flex items-center justify-center space-x-2" data-id="element-603">
            <Sparkles className="w-8 h-8 text-yellow-500" data-id="element-604" />
            <span data-id="element-605">Can You Spot the Myth? 🎯</span>
          </h2>
          <p className="text-gray-600 dark:text-gray-300 max-w-2xl mx-auto" data-id="element-606">
            Play fun games to bust common myths about growing up, relationships,
            and your body!
          </p>
        </div>
        {/* Games Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-607">
          {mythGames.map(game => <button key={game.id} onClick={() => setSelectedGame(game.id)} className="Card with shadow effect, hover scale and transition" data-id="element-608">
              <div className="w-16 h-16 bg-gradient-to-br from-yellow-500 to-orange-500 rounded-2xl flex items-center justify-center mb-4 shadow-lg group-hover:scale-110 transition-transform" data-id="element-609">
                <Zap className="w-9 h-9 text-white" data-id="element-610" />
              </div>
              <h3 className="Headline with color change on hover, dark mode" data-id="element-611">
                {game.title}
              </h3>
              <p className="text-gray-600 dark:text-gray-300 mb-4" data-id="element-612">
                {game.description}
              </p>
              <div className="flex items-center justify-between" data-id="element-613">
                <span className="text-sm text-gray-500 dark:text-gray-400" data-id="element-614">
                  {game.myths.length} statements
                </span>
                <div className="Icon with hover effect, dark mode" data-id="element-615">
                  Play Now{' '}
                  <span className="ml-2 group-hover:translate-x-2 transition-transform" data-id="element-616">
                    →
                  </span>
                </div>
              </div>
            </button>)}
        </div>
      </div>
    </div>;
}